import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft } from "@untitledui/icons";
import { useEffect, useState } from "react";
import {
  fetchTargetCollaborationCreators,
  targetCollaborationCreatorsKey,
  useTargetCollaborationDetail,
} from "@/api/collaborations";
import { useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { useFreshen } from "@/api/use-freshen";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMerchant } from "@/providers/MerchantProvider";
import type { targetCollaborationDetailRoute } from "@/router";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { CollaborationCreatorsTable } from "./collaboration-creators-table";
import { CollaborationDetailHeader } from "./collaboration-detail-header";
import type { CreatorStatusTab } from "./collaboration-table-columns";
import { EditTargetCollaborationStepper } from "./edit-target-collaboration-stepper";
import { CollaborationInsightsPanel } from "./insights";
import { ViewAutomationTriggersModal } from "./view-automation-triggers-modal";

/**
 * Target Collaboration Detail Page
 *
 * Displays detailed information about a specific target collaboration including:
 * - Header with collaboration name, type, commission rate, products count, and valid until date
 * - Tabs for filtering creators by status
 * - Table of creators in the collaboration with search and actions
 *
 * This page is accessed by clicking on a collaboration row in the Target Collaborations table.
 */
export const TargetCollaborationDetail = () => {
  const { collaborationId } = useParams({ strict: false }) as { collaborationId: string };
  const navigate = useNavigate() as ReturnType<(typeof targetCollaborationDetailRoute)["useNavigate"]>;
  const { tab: activeTab } = useSearch({ strict: false }) as ReturnType<
    (typeof targetCollaborationDetailRoute)["useSearch"]
  >;
  const merchant = useMerchant();
  const [editOpen, setEditOpen] = useState(false);
  const [editInitialStepId, setEditInitialStepId] = useState<string>();
  const [automationsOpen, setAutomationsOpen] = useState(false);
  const [breakdownFilter, setBreakdownFilter] = useState<string | null>(null);
  const newCollabsGroupsEnabled = useFeatureFlag("F-new-collabs-groups");
  const goBackToCollaborations = () => {
    navigate({
      to: newCollabsGroupsEnabled
        ? "/merchant/$merchantId/collabs/groups/target"
        : "/merchant/$merchantId/collabs/target",
      params: { merchantId: merchant },
    });
  };
  const setActiveTab = (next: CreatorStatusTab) => {
    navigate({ search: (prev) => ({ ...prev, tab: next as typeof activeTab }) });
  };

  const { data: collaboration, isLoading, error } = useTargetCollaborationDetail(collaborationId);
  const queryClient = useQueryClient();
  const scope = useScopedMerchantClient();

  // Once the detail resolves, prefetch the first page of creators for every non-empty tab.
  // The prefetch uses the exact same queryKey + queryFn as useTargetCollaborationCreators, so
  // clicking a tab with count > 0 resolves instantly from cache.
  useEffect(() => {
    if (!collaboration || !scope.merchantId || !collaborationId) return;

    const { statusCounts: counts } = collaboration;
    const tabsToPreload: Array<[string, number]> = [
      ["invited", counts.invited],
      ["sample_requested", counts.sampleRequested],
      ["sample_status", counts.sampleStatus],
      ["pending_post", counts.pendingPost],
      ["posted_video", counts.postedVideo],
      ["posted_no_gmv", counts.postedNoGmv],
      ["cancelled", counts.cancelled],
      ["removed", counts.removed],
    ];

    for (const [status, tabCount] of tabsToPreload) {
      if (tabCount <= 0) continue;
      const opts = { status: status as "invited", pagination: { limit: 20, offset: 0 } };
      queryClient.prefetchQuery({
        queryKey: targetCollaborationCreatorsKey(scope, collaborationId, opts),
        queryFn: () => fetchTargetCollaborationCreators(scope, collaborationId, opts),
      });
    }
  }, [collaboration?.collaborationId, scope, collaborationId, queryClient]);

  // Kick off a background refresh from TikTok. Server-side cooldown gates frequency;
  // SSE invalidation re-fetches this query when the refresh completes with changes.
  useFreshen("collab", collaborationId ? [collaborationId] : []);

  // Get status counts from the collaboration data
  const statusCounts = collaboration?.statusCounts ?? {
    all: 0,
    invited: 0,
    sampleRequested: 0,
    sampleStatus: 0,
    pendingPost: 0,
    postedVideo: 0,
    postedNoGmv: 0,
    cancelled: 0,
    removed: 0,
  };

  const isAutoApproved =
    !!collaboration?.hasFreeSample &&
    (!!collaboration?.isSampleApprovalExempt || collaboration?.minRequirements != null);

  const tabs = [
    { id: "all" as const, label: "All", badge: statusCounts.all },
    { id: "invited" as const, label: "Invited", badge: statusCounts.invited },
    ...(collaboration?.hasFreeSample
      ? [
          {
            id: "sample_requested" as const,
            label: "Sample Requested",
            badge: isAutoApproved ? "Auto" : statusCounts.sampleRequested,
          },
        ]
      : []),
    {
      id: "sample_status" as const,
      label: "Sample Status",
      badge: statusCounts.sampleStatus,
    },
    {
      id: "pending_post" as const,
      label: "Pending Post",
      badge: statusCounts.pendingPost,
    },
    {
      id: "posted_no_gmv" as const,
      label: "Posted No GMV",
      badge: statusCounts.postedNoGmv,
    },
    {
      id: "posted_video" as const,
      label: "Generated GMV",
      badge: statusCounts.postedVideo,
    },
    {
      id: "cancelled" as const,
      label: "Cancelled",
      badge: statusCounts.cancelled,
    },
    {
      id: "removed" as const,
      label: "Removed Creators",
      badge: statusCounts.removed,
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <LoadingIndicator label="Loading collaboration..." size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !collaboration) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <EmptyState size="md">
          <div className="pb-6">
            <EmptyState.FeaturedIcon color="gray" />
          </div>
          <EmptyState.Content>
            <EmptyState.Title>Collaboration not found</EmptyState.Title>
            <EmptyState.Description>
              The collaboration you're looking for doesn't exist or you don't have access to it.
            </EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button color="secondary" size="md" onClick={goBackToCollaborations}>
              <div className="flex items-center">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Collaborations
              </div>
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  const handleTabChange = (key: string | number) => {
    navigate({ search: (prev) => ({ ...prev, tab: key as CreatorStatusTab }) });
  };

  return (
    <div className="flex flex-col w-full min-h-screen items-center bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col px-16 pt-8 pb-8`}>
        {/* Back button */}
        <Button
          color="tertiary"
          size="sm"
          onClick={goBackToCollaborations}
          className="text-quaternary hover:text-primary pl-0 w-fit mb-3"
        >
          <div className="flex items-center">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Collaborations
          </div>
        </Button>

        {/* Header with collaboration info */}
        <CollaborationDetailHeader
          collaboration={collaboration}
          sampleRequestedCount={statusCounts.sampleRequested}
          showRecommendations
          onEdit={() => {
            setEditInitialStepId(undefined);
            setEditOpen(true);
          }}
          onViewAutomations={() => setAutomationsOpen(true)}
        />

        <EditTargetCollaborationStepper
          open={editOpen}
          onOpenChange={setEditOpen}
          collaboration={collaboration}
          initialStepId={editInitialStepId}
        />
        <ViewAutomationTriggersModal
          open={automationsOpen}
          onOpenChange={setAutomationsOpen}
          scope={{ kind: "collaboration", collaborationId: collaboration.collaborationId }}
          onEdit={() => {
            setEditInitialStepId("automation");
            setEditOpen(true);
          }}
        />

        {/* Collaboration Insights Panel — between header and creator tabs */}
        <CollaborationInsightsPanel
          collaborationId={collaborationId}
          showRecommendations
          onFilterChange={setActiveTab}
          onBreakdownFilterChange={setBreakdownFilter}
        />

        {/* Status filter tabs */}
        <div id="collaboration-creators-list" className="flex items-center justify-between mb-6 scroll-mt-16">
          <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange}>
            <Tabs.List type="underline" items={tabs}>
              {(tab) => <Tabs.Item {...tab} />}
            </Tabs.List>
          </Tabs>
        </div>

        {/* Creators table */}
        <CollaborationCreatorsTable
          collaborationId={collaborationId}
          collaborationName={collaboration.name}
          statusFilter={activeTab}
          isAutoApproved={isAutoApproved}
          breakdownFilter={breakdownFilter}
          onClearBreakdownFilter={() => setBreakdownFilter(null)}
        />
      </div>
    </div>
  );
};
